import { useEffect, useState, useCallback } from 'react';
import { Search, Filter, Trophy } from 'lucide-react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Results = () => {
  const [results, setResults] = useState([]);
  const [events, setEvents] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = ['all', 'Elite', 'Sub-23', 'Master', 'Feminino'];

  const filterResults = useCallback(() => {
    let filtered = results;

    if (selectedEvent !== 'all') {
      filtered = filtered.filter(result => result.event_id === selectedEvent);
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(result => result.category === selectedCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(result =>
        result.participant_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredResults(filtered);
  }, [results, selectedEvent, selectedCategory, searchTerm]);

  const fetchData = async () => {
    try {
      const [resultsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/results`),
        axios.get(`${API}/events`),
      ]);
      setResults(resultsRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterResults();
  }, [selectedEvent, selectedCategory, searchTerm, results, filterResults]);

  const getMedalColor = (position) => {
    if (position === 1) return 'text-yellow-500';
    if (position === 2) return 'text-gray-400';
    if (position === 3) return 'text-amber-700';
    return 'text-foreground';
  };

  return (
    <div data-testid="results-page" className="min-h-screen py-24 md:py-32 px-6 md:px-12 lg:px-24">
      <div
        className="relative h-[300px] bg-cover bg-center rounded-lg mb-12 -mx-6 md:-mx-12 lg:-mx-24"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1629017732267-f3a52fe5aff4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxjeWNsaXN0JTIwY2VsZWJyYXRpbmclMjBwb2RpdW18ZW58MHx8fHwxNzcwMjM3MDUzfDA&ixlib=rb-4.1.0&q=85)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-primary/80 rounded-lg"></div>
        <div className="relative h-full flex items-end px-6 md:px-12 lg:px-24 pb-8">
          <div>
            <h1 className="text-4xl md:text-6xl font-heading font-black tracking-tight uppercase text-white text-shadow-sm mb-2">
              Resultados
            </h1>
            <p className="text-base md:text-lg text-white/90">
              Confira os resultados oficiais de todas as competições
            </p>
          </div>
        </div>
      </div>

      <div data-testid="results-filters" className="mb-8 space-y-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-foreground/50" />
          <Input
            data-testid="search-input"
            type="text"
            placeholder="Buscar participante..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-12 bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-bold uppercase tracking-widest text-primary mb-2 block">
              Evento
            </label>
            <select
              data-testid="filter-event"
              value={selectedEvent}
              onChange={(e) => setSelectedEvent(e.target.value)}
              className="w-full bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
            >
              <option value="all">Todos os Eventos</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-bold uppercase tracking-widest text-primary mb-2 block">
              Categoria
            </label>
            <select
              data-testid="filter-category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full bg-white border-2 border-border focus:border-accent focus:ring-0 rounded-sm h-12 px-4 font-body"
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category === 'all' ? 'Todas as Categorias' : category}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-card border border-border rounded-lg p-8">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-16 bg-secondary/50 rounded"></div>
            ))}
          </div>
        </div>
      ) : filteredResults.length > 0 ? (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="results-table" className="w-full">
              <thead className="bg-primary text-primary-foreground">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-widest">Posição</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-widest">Participante</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-widest">Evento</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-widest">Categoria</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-widest">Tempo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredResults.map((result, index) => (
                  <tr
                    key={result.id}
                    data-testid={`result-row-${index}`}
                    className="hover:bg-secondary/30 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {result.position <= 3 && <Trophy className={`w-5 h-5 ${getMedalColor(result.position)}`} />}
                        <span className={`text-lg font-bold ${getMedalColor(result.position)}`}>
                          {result.position}º
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-bold text-foreground">{result.participant_name}</td>
                    <td className="px-6 py-4 text-foreground/70">{result.event_name}</td>
                    <td className="px-6 py-4">
                      <span className="text-xs uppercase tracking-widest font-bold text-primary bg-secondary px-3 py-1 rounded-sm">
                        {result.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-mono text-foreground">{result.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Trophy className="w-16 h-16 text-foreground/30 mx-auto mb-4" />
          <p className="text-xl text-foreground/50 mb-4">Nenhum resultado encontrado.</p>
          <Button
            data-testid="clear-filters-button"
            onClick={() => {
              setSearchTerm('');
              setSelectedEvent('all');
              setSelectedCategory('all');
            }}
            className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-widest font-bold px-8 py-4 rounded-sm"
          >
            Limpar Filtros
          </Button>
        </div>
      )}
    </div>
  );
};

export default Results;